import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext();

// Use sessionStorage only - each tab has completely independent storage
const getStorageToken = () => sessionStorage.getItem('token');
const setStorageToken = (token) => {
  if (token) {
    sessionStorage.setItem('token', token);
  }
};
const removeStorageToken = () => sessionStorage.removeItem('token');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStorageToken());
  const [loading, setLoading] = useState(true);

  // NO storage event listener - each tab is completely independent
  // NO localStorage usage - only sessionStorage per tab

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      removeStorageToken();
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, twoFactorToken = '') => {
    try {
      const response = await authAPI.login(email, password, twoFactorToken);
      
      // If 2FA is required, return the response without setting token
      if (response.data.requires2FA) {
        return response.data;
      }
      
      setStorageToken(response.data.token);
      setToken(response.data.token);
      setUser(response.data.user);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await authAPI.register(name, email, password);
      setStorageToken(response.data.token);
      setToken(response.data.token);
      setUser(response.data.user);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    removeStorageToken();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
